import { useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Volume2, VolumeX, SkipForward, X, Flame, BookOpen, Leaf, Stamp, UtensilsCrossed, ArrowLeft, Info, Rotate3d, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, USER_ID, money, mkAudio, track } from "../api";
import { addPick } from "../picks";
import { shareDishCard } from "../shareCard";
import "../cinematic.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const Cinematic3D = lazy(() => import("../Cinematic3D"));
const PHASE_TIMES = [0, 1000, 2000, 4000, 5000, 6000, 8000, 10000];
// phase: 0 push · 1 bowl · 2 fill · 3 steam · 4 sides · 5 orbit · 6 hero · 7 done
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const SPICE_DOTS = Array.from({ length: 14 }, (_, i) => ({
  left: 6 + ((i * 37) % 88), top: 25 + ((i * 53) % 60),
  size: 2 + (i % 3), dur: 6 + (i % 5) * 1.7, delay: (i % 7) * 0.9,
  color: i % 3 === 0 ? "#E25822" : i % 3 === 1 ? "#D4AF37" : "#8FBF6A",
}));

const splitSentences = (s) => (s || "").match(/[^.!?।]+[.!?।]+/g)?.map((x) => x.trim()) || [s];

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add("on"); io.disconnect(); } }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className="cin-reveal" style={delay ? { transitionDelay: `${delay}ms` } : undefined}>{children}</div>;
};

export default function CinematicDish() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState(reduced() ? 7 : 0);
  const [panel, setPanel] = useState(null); // story | ingredients | nutrition | spice
  const [hotspot, setHotspot] = useState(null);
  const [mode3d, setMode3d] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const secretSeen = useRef(false);
  const [sharing, setSharing] = useState(false);
  const [paSubs, setPaSubs] = useState(() => localStorage.getItem("xp_subs_pa") === "1");
  const [audioOn, setAudioOn] = useState(false);
  const [fav, setFav] = useState(false);
  const [ready, setReady] = useState(false);
  const [story, setStory] = useState({ playing: false, line: -1, loading: false });
  const timers = useRef([]);
  const tiltRef = useRef(null);
  const stageRef = useRef(null);
  const phaseRef = useRef(phase);
  const pointers = useRef(new Map());
  const drag = useRef(null);
  const zoom = useRef(1);
  const audioCtx = useRef(null);
  const ambientGain = useRef(null);
  const narr = useRef(null);

  useEffect(() => {
    api.get(`/cinematic/${slug}`).then((r) => { setData(r.data); track("cinematic_opened", { slug }); }).catch(() => setData({ missing: true }));
    api.get(`/passport/${USER_ID}`).then((r) => setFav((r.data.user.favorites || []).includes(slug))).catch(() => {});
  }, [slug]);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // preload + decode the key layers so the hero never paints half-loaded
  useEffect(() => {
    if (!data || data.missing) return;
    const A2 = data.assets;
    if (!A2) { setReady(true); return; }
    let done = false;
    const key = [A2.scene, A2.hero, A2.bowl].map((src) => { const i = new Image(); i.src = src; return i.decode ? i.decode().catch(() => {}) : Promise.resolve(); });
    [A2.naan, A2.sides, A2.cilantro].forEach((src) => { const i = new Image(); i.src = src; });
    const t = setTimeout(() => { if (!done) setReady(true); }, 2500);
    Promise.all(key).then(() => { done = true; clearTimeout(t); setReady(true); });
    return () => clearTimeout(t);
  }, [data]);

  const jumpToEnd = useCallback(() => {
    timers.current.forEach(clearTimeout);
    setPhase(7);
  }, []);

  useEffect(() => {
    if (!data || data.missing || !ready || reduced()) return;
    timers.current = PHASE_TIMES.slice(1).map((t, i) => setTimeout(() => setPhase(i + 1), t));
    return () => timers.current.forEach(clearTimeout);
  }, [data, ready]);

  // ---- WebAudio: soft ambient + sizzle (only after user enables sound) ----
  const noiseBuf = (ctx, secs) => {
    const b = ctx.createBuffer(1, ctx.sampleRate * secs, ctx.sampleRate);
    const d = b.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) { const w = Math.random() * 2 - 1; last = (last + 0.02 * w) / 1.02; d[i] = last * 3; }
    return b;
  };
  const enableAudio = () => {
    if (audioOn) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtx.current = ctx;
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf(ctx, 4); src.loop = true;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 320;
      const g = ctx.createGain(); g.gain.value = 0.018;
      src.connect(lp).connect(g).connect(ctx.destination);
      src.start();
      ambientGain.current = g;
      setAudioOn(true);
    } catch { setAudioOn(true); }
  };
  const sizzle = useCallback(() => {
    const ctx = audioCtx.current;
    if (!ctx) return;
    const src = ctx.createBufferSource();
    const b = ctx.createBuffer(1, ctx.sampleRate * 1.4, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.6);
    src.buffer = b;
    const bp = ctx.createBiquadFilter(); bp.type = "highpass"; bp.frequency.value = 3200;
    const g = ctx.createGain(); g.gain.value = 0.05;
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start();
  }, []);
  const muteAll = () => {
    setAudioOn(false);
    if (ambientGain.current) ambientGain.current.gain.value = 0;
    if (narr.current) { narr.current.pause(); narr.current = null; }
    setStory((s) => ({ ...s, playing: false }));
  };
  useEffect(() => {
    if (phase === 2 && audioOn) sizzle();
  }, [phase, audioOn, sizzle]);
  useEffect(() => () => { audioCtx.current?.close?.(); narr.current?.pause?.(); }, []);

  // ---- drag tilt / pinch zoom ----
  const setVars = (px, py, zz) => {
    const el = tiltRef.current;
    if (!el) return;
    el.style.setProperty("--px", String(px));
    el.style.setProperty("--py", String(py));
    el.style.setProperty("--zz", String(zz));
  };
  const onDown = (e) => {
    if (phase < 6) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      const el = tiltRef.current;
      drag.current = { x: e.clientX, y: e.clientY,
        px: parseFloat(el?.style.getPropertyValue("--px")) || 0,
        py: parseFloat(el?.style.getPropertyValue("--py")) || 0 };
    }
  };
  const onMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length >= 2) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (!drag.current?.pinch) drag.current = { ...drag.current, pinch: dist, z0: zoom.current };
      zoom.current = Math.min(1.8, Math.max(1, (drag.current.z0 * dist) / drag.current.pinch));
      setVars(parseFloat(tiltRef.current?.style.getPropertyValue("--px")) || 0,
        parseFloat(tiltRef.current?.style.getPropertyValue("--py")) || 0, zoom.current);
    } else if (drag.current) {
      const px = Math.max(-1, Math.min(1, drag.current.px + (e.clientX - drag.current.x) / 260));
      const py = Math.max(-1, Math.min(1, drag.current.py + (e.clientY - drag.current.y) / 260));
      setVars(px, py, zoom.current);
    }
  };
  const onUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) drag.current = null;
    else if (drag.current) drag.current.pinch = null;
  };
  // native non-passive wheel listener: zoom without scrolling the page
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const h = (e) => {
      if (phaseRef.current < 6) return;
      e.preventDefault();
      zoom.current = Math.min(1.8, Math.max(1, zoom.current - e.deltaY / 600));
      setVars(parseFloat(tiltRef.current?.style.getPropertyValue("--px")) || 0,
        parseFloat(tiltRef.current?.style.getPropertyValue("--py")) || 0, zoom.current);
    };
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, [ready]);

  // ---- narrated story ----
  const playStory = async () => {
    setPanel("story");
    if (story.playing) return;
    setStory({ playing: false, line: -1, loading: true });
    try {
      const r = await api.post(`/cinematic/${slug}/narration`, { user_id: USER_ID });
      if (!r.data.audio_url) { setStory({ playing: false, line: 0, loading: false }); return; }
      enableAudio();
      const a = mkAudio(`${BACKEND_URL}${r.data.audio_url}`);
      narr.current = a;
      const lines = splitSentences(data.script);
      const cum = [];
      let acc = 0;
      lines.forEach((l) => { acc += l.length; cum.push(acc); });
      a.ontimeupdate = () => {
        if (!a.duration) return;
        const p = (a.currentTime / a.duration) * acc;
        setStory((s) => ({ ...s, line: Math.min(lines.length - 1, cum.findIndex((c) => p <= c)) }));
      };
      a.onended = () => setStory({ playing: false, line: -1, loading: false });
      await a.play();
      setStory({ playing: true, line: 0, loading: false });
      track("cinematic_story_played", { slug });
    } catch { setStory({ playing: false, line: -1, loading: false }); }
  };
  const stopStory = () => {
    narr.current?.pause();
    narr.current = null;
    setStory({ playing: false, line: -1, loading: false });
  };

  const toggleFav = async () => {
    const r = await api.post(`/favorites/${slug}`, { user_id: USER_ID });
    setFav(r.data.favorited);
    toast.success(r.data.favorited ? "Stamped into your passport" : "Removed from your passport",
      { style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" } });
  };

  const doShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const result = await shareDishCard(data);
      if (result !== "cancelled") {
        track("cinematic_share", { slug });
        toast.success(result === "shared" ? "Shared — spread the flavour!" : "Card saved — share it anywhere",
          { style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" } });
      }
    } catch { toast.error("Could not create the card — try again"); }
    setSharing(false);
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={22} className="animate-spin text-[#D4AF37]" /></div>;
  if (data.missing) return <div className="p-10 text-center text-white/50 text-sm">Dish not found. <Link to="/menu" className="text-[#D4AF37] underline">Back to menu</Link></div>;
  if (!ready) return <div data-testid="cinematic-loading" className="min-h-[60vh] flex items-center justify-center bg-[#050505]"><Loader2 size={22} className="animate-spin text-[#D4AF37]" /></div>;

  const A = data.assets;
  const done = phase >= 6;
  const lines = splitSentences(data.script);
  const paLines = data.script_pa ? splitSentences(data.script_pa) : null;

  return (
    <div data-testid="cinematic-page" className="bg-[#050505] min-h-screen">
      {/* STAGE */}
      <div ref={stageRef} className="cin-stage cin-stage-h relative w-full overflow-hidden select-none"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        {mode3d && A ? (
          <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center"><Loader2 size={22} className="animate-spin text-[#D4AF37]" /></div>}>
            <Cinematic3D assets={A} vessel={data.vessel}
              onFullSpin={() => { if (!secretSeen.current && data.secret) { secretSeen.current = true; setSecretOpen(true); track("cinematic_secret", { slug }); } }} />
          </Suspense>
        ) : done && data.hero_video ? (
          <div className="absolute inset-0" data-testid="cin-video-slot">
            <video data-testid="cin-hero-video" src={data.hero_video.startsWith("http") ? data.hero_video : `${BACKEND_URL}${data.hero_video}`}
              autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
            <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 to-transparent" />
          </div>
        ) : (
        <div ref={tiltRef} className={`cin-tilt absolute inset-0 ${done ? "cin-kb" : ""}`}>
          {/* scene */}
          {A ? (
            <img src={A.scene} alt="" aria-hidden="true" className={`cin-layer cin-par-1 absolute inset-0 w-full h-full object-cover cin-scene ${phase >= 0 ? "on" : ""}`} />
          ) : (
            <img src={data.dish.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          )}
          {/* spice particles */}
          {phase >= 0 && SPICE_DOTS.map((s, i) => (
            <span key={i} aria-hidden="true" className="cin-spice absolute rounded-full pointer-events-none"
              style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, background: s.color, opacity: 0.5, animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s` }} />
          ))}

          <div className={phase === 5 ? "cin-orbit absolute inset-0" : "absolute inset-0"}>
            {A && (
              <>
                {/* bowl arrives */}
                {phase >= 1 && (
                  <div className="cin-layer cin-par-2c cin-dish-pos absolute">
                    <div className={phase === 1 ? "cin-shake" : ""}>
                      <img src={A.bowl} alt="" aria-hidden="true" className={`w-full ${phase === 1 ? "cin-drop" : ""}`} style={{ filter: "drop-shadow(0 26px 30px rgba(0,0,0,0.65))" }} />
                    </div>
                  </div>
                )}
                {/* butter chicken fills */}
                {phase >= 2 && (
                  <div className="cin-layer cin-par-2c cin-dish-pos absolute">
                    <img data-testid="cinematic-hero-dish" src={A.hero} alt={data.dish.name} className={phase === 2 ? "cin-fill w-full" : "w-full"} style={{ filter: "drop-shadow(0 26px 32px rgba(0,0,0,0.7))" }} />
                    {/* butter glint */}
                    {phase >= 3 && <span aria-hidden="true" className="cin-glint absolute rounded-full pointer-events-none" style={{ left: "44%", top: "42%", width: "14%", height: "12%", background: "radial-gradient(circle, rgba(255,240,190,0.9), transparent 70%)" }} />}
                    {/* steam */}
                    {phase >= 3 && [0, 1, 2].map((i) => (
                      <span key={i} aria-hidden="true" className="cin-steam absolute pointer-events-none"
                        style={{ left: `${34 + i * 15}%`, top: "6%", width: 26, height: 60, borderRadius: "50%", background: "radial-gradient(ellipse, rgba(255,255,255,0.35), transparent 70%)", filter: "blur(6px)", animationDelay: `${i * 1.1}s` }} />
                    ))}
                    {/* ingredient hotspots */}
                    {done && data.ingredients.map((ing) => (
                      <button key={ing.id} data-testid={`cin-hotspot-${ing.id}`} aria-label={`About ${ing.name}`}
                        onClick={(e) => { e.stopPropagation(); setHotspot(hotspot?.id === ing.id ? null : ing); }}
                        className={`cin-hotspot absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${hotspot?.id === ing.id ? "bg-[#D4AF37] border-white scale-125" : "bg-black/50 border-[#D4AF37]"}`}
                        style={{ left: `${ing.x}%`, top: `${ing.y}%` }} />
                    ))}
                  </div>
                )}
                {/* cilantro falls */}
                {phase >= 3 && (
                  <img src={A.cilantro} alt="" aria-hidden="true" className={`cin-layer cin-par-3r cin-cil-pos absolute ${phase === 3 ? "cin-cilantro-fall" : ""}`}
                    style={{ filter: "drop-shadow(0 10px 10px rgba(0,0,0,0.5))" }} />
                )}
                {/* naan + sides */}
                {phase >= 4 && (
                  <img src={A.naan} alt={data.bread_alt || "Garlic naan"} className={`cin-layer cin-par-3 cin-naan-pos absolute ${phase === 4 ? "cin-slide-r" : ""}`}
                    style={{ filter: "drop-shadow(0 18px 22px rgba(0,0,0,0.6))" }} />
                )}
                {phase >= 4 && (
                  <img src={A.sides} alt="Chutney, onions and lemon" className={`cin-layer cin-par-3 cin-sides-pos absolute ${phase === 4 ? "cin-slide-l" : ""}`}
                    style={{ filter: "drop-shadow(0 18px 22px rgba(0,0,0,0.6))" }} />
                )}
              </>
            )}
            {!A && (
              <div className="absolute inset-0 flex items-center justify-center">
                <img data-testid="cinematic-hero-dish" src={data.dish.image} alt={data.dish.name} className="w-[min(60vw,420px)] rounded-2xl shadow-2xl" />
              </div>
            )}
          </div>
        </div>
        )}
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

        {/* vignette + DOF edges */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 46%, transparent 42%, rgba(0,0,0,0.55) 82%, rgba(0,0,0,0.85) 100%)" }} />
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-16 pointer-events-none" style={{ backdropFilter: "blur(2px)", maskImage: "linear-gradient(to bottom, black, transparent)" }} />

        {/* top controls */}
        <div className="absolute top-3 inset-x-3 flex items-center justify-between z-20">
          <Link to="/menu" data-testid="cin-back" aria-label="Back to menu"
            className="w-9 h-9 rounded-full bg-black/55 border border-white/20 text-white/80 flex items-center justify-center backdrop-blur-md"><ArrowLeft size={15} /></Link>
          <div className="flex gap-2">
            {!done && (
              <button data-testid="cin-skip-intro" onClick={jumpToEnd}
                className="h-9 px-4 rounded-full bg-black/55 border border-white/20 text-white/85 text-[10px] font-black tracking-widest backdrop-blur-md flex items-center gap-1.5">
                <SkipForward size={12} /> SKIP INTRO
              </button>
            )}
            {done && A?.hero_top && (
              <button data-testid="cin-3d-toggle" aria-label={mode3d ? "Back to photo view" : "View in 3D"}
                onClick={() => { setHotspot(null); const n = !mode3d; setMode3d(n); if (n) track("cinematic_3d", { slug }); }}
                className={`h-9 px-4 rounded-full border text-[10px] font-black tracking-widest backdrop-blur-md flex items-center gap-1.5 ${mode3d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-black/55 border-white/20 text-white/85"}`}>
                <Rotate3d size={13} /> {mode3d ? "PHOTO" : "3D"}
              </button>
            )}
            <button data-testid="cin-mute" onClick={audioOn ? muteAll : enableAudio} aria-label={audioOn ? "Mute" : "Enable sound"}
              className={`w-9 h-9 rounded-full border flex items-center justify-center backdrop-blur-md ${audioOn ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-black/55 border-white/20 text-white/80"}`}>
              {audioOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
          </div>
        </div>

        {/* secret unlocked card */}
        {secretOpen && data.secret && (
          <div data-testid="cin-secret-card" onClick={() => setSecretOpen(false)} role="button" aria-label="Secret unlocked — tap to dismiss"
            className="absolute top-16 inset-x-0 mx-auto z-30 w-[min(88vw,380px)] rounded-2xl bg-[#1A1206]/95 border border-[#D4AF37] backdrop-blur-md p-4 cin-rise cursor-pointer">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black flex items-center gap-1.5"><Sparkles size={12} /> SECRET UNLOCKED</p>
              <button data-testid="cin-secret-close" onClick={(e) => { e.stopPropagation(); setSecretOpen(false); }} aria-label="Close" className="text-white/50"><X size={14} /></button>
            </div>
            <p className="text-xs text-white/85 leading-relaxed mt-1.5">{data.secret}</p>
          </div>
        )}

        {/* hotspot card */}
        {hotspot && (
          <div data-testid="cin-hotspot-card" onClick={() => setHotspot(null)} role="button" aria-label={`${hotspot.name} — tap to dismiss`}
            className="absolute top-16 inset-x-0 mx-auto z-20 w-[min(88vw,360px)] rounded-2xl bg-black/75 border border-[#D4AF37]/50 backdrop-blur-md p-4 cin-rise cursor-pointer">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-black text-[#D4AF37]">{hotspot.name}</p>
              <button data-testid="cin-hotspot-close" onClick={() => setHotspot(null)} aria-label="Close" className="text-white/50"><X size={14} /></button>
            </div>
            <p className="text-xs text-white/75 leading-relaxed mt-1">{hotspot.desc}</p>
          </div>
        )}

        {/* title + actions */}
        {done && (
          <div className="absolute inset-x-0 bottom-0 z-10 px-4 pb-4 pt-10 text-center pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.95) 55%, rgba(0,0,0,0.6) 80%, transparent)" }}>
            <p className="cin-rise text-[10px] tracking-[0.4em] text-[#D4AF37] font-bold">URBAN BY VEERGI PRESENTS</p>
            <h1 data-testid="cin-title" className="cin-rise font-display text-3xl sm:text-5xl font-bold tracking-tight mt-1" style={{ animationDelay: "0.12s" }}>{data.headline}</h1>
            <p className="cin-rise text-sm text-white/65 italic mt-1" style={{ animationDelay: "0.22s" }}>{data.sub} · {money(data.dish.price)}</p>
            <p className="cin-rise text-[10px] text-white/35 mt-1.5 tracking-widest" style={{ animationDelay: "0.3s" }}>
              {mode3d ? (data.secret && !secretSeen.current ? "SPIN IT ONE FULL ROUND — A SECRET AWAITS" : "SPIN THE DISH WITH YOUR FINGER") : <><span className="hidden sm:inline">DRAG TO ROTATE · PINCH TO ZOOM · </span>TAP THE GOLD DOTS</>}
            </p>
            <div className="cin-rise flex flex-wrap justify-center gap-2 mt-3 pointer-events-auto" style={{ animationDelay: "0.38s" }}>
              <button data-testid="cin-order" onClick={() => { addPick(data.dish); track("cinematic_order", { slug }); }}
                className="h-11 px-6 rounded-full bg-[#D4AF37] text-black text-xs font-black tracking-widest flex items-center gap-1.5"><UtensilsCrossed size={13} /> ORDER NOW</button>
              <button data-testid="cin-story" onClick={playStory}
                className="h-11 px-5 rounded-full border border-[#E25822]/70 text-[#E25822] text-xs font-black tracking-widest flex items-center gap-1.5"><BookOpen size={13} /> HEAR THE STORY</button>
              <button data-testid="cin-ingredients" onClick={() => setPanel(panel === "ingredients" ? null : "ingredients")}
                className="h-11 px-5 rounded-full border border-white/25 text-white/80 text-xs font-black tracking-widest flex items-center gap-1.5"><Leaf size={13} /> INGREDIENTS</button>
              <button data-testid="cin-nutrition" onClick={() => setPanel(panel === "nutrition" ? null : "nutrition")}
                className="h-11 px-5 rounded-full border border-white/25 text-white/80 text-xs font-black tracking-widest flex items-center gap-1.5"><Info size={13} /> NUTRITION</button>
              <button data-testid="cin-spice" onClick={() => setPanel(panel === "spice" ? null : "spice")}
                className="h-11 px-5 rounded-full border border-white/25 text-white/80 text-xs font-black tracking-widest flex items-center gap-1.5"><Flame size={13} /> SPICE LEVEL</button>
              <button data-testid="cin-passport" onClick={toggleFav}
                className={`h-11 px-5 rounded-full border text-xs font-black tracking-widest flex items-center gap-1.5 ${fav ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/25 text-white/80"}`}>
                <Stamp size={13} /> {fav ? "IN YOUR PASSPORT" : "ADD TO PASSPORT"}
              </button>
              <button data-testid="cin-share" onClick={doShare} aria-label="Share this dish card"
                className="h-11 px-5 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-black tracking-widest flex items-center gap-1.5">
                {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} SHARE
              </button>
            </div>
          </div>
        )}

        {/* enable sound pill during timeline */}
        {!done && !audioOn && (
          <button data-testid="cin-sound-pill" onClick={enableAudio}
            className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 h-9 px-5 rounded-full bg-black/60 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-black tracking-widest backdrop-blur-md flex items-center gap-1.5">
            <Volume2 size={12} /> TAP FOR SOUND
          </button>
        )}
      </div>

      {/* SCROLL STORY */}
      {data.script && (
        <section data-testid="cin-scroll-story" className="px-6 pt-10 pb-2 max-w-3xl mx-auto text-center">
          <Reveal><h2 data-testid="cin-reveal-name" className="font-display text-4xl sm:text-5xl font-bold text-[#D4AF37]">{data.headline}</h2></Reveal>
          <Reveal><p className="font-display italic text-white/60 mt-2">{data.sub}</p></Reveal>
          <div className="mt-6 space-y-3">
            {lines.map((l, i) => (
              <Reveal key={i} delay={Math.min(i * 110, 660)}><p data-testid={`cin-scroll-line-${i}`} className="text-base text-white/75 leading-relaxed">{l}</p></Reveal>
            ))}
          </div>
          <Reveal>
            <button data-testid="cin-reveal-order" onClick={() => { addPick(data.dish); track("cinematic_order", { slug }); }}
              className="cin-pulse mt-8 px-9 py-3.5 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest">
              ORDER NOW — {money(data.dish.price)}
            </button>
          </Reveal>
        </section>
      )}

      {/* PANELS */}
      {panel === "story" && (
        <div data-testid="cin-story-panel" className="mx-4 mt-4 rounded-2xl border border-[#E25822]/40 bg-[#120B06] p-5 cin-rise">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-black">THE STORY — NARRATED BY VEERGI</p>
            <div className="flex gap-2">
              {paLines && (
                <button data-testid="cin-subs-pa-toggle" aria-pressed={paSubs}
                  onClick={() => setPaSubs((v) => { localStorage.setItem("xp_subs_pa", v ? "0" : "1"); return !v; })}
                  className={`h-8 px-3 rounded-full border text-[10px] font-black ${paSubs ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/25 text-white/70"}`}>ਪੰਜਾਬੀ</button>
              )}
              {(story.playing || story.loading) && (
                <button data-testid="cin-story-skip" onClick={stopStory}
                  className="h-8 px-4 rounded-full border border-white/25 text-white/70 text-[9px] font-black tracking-widest flex items-center gap-1"><SkipForward size={11} /> SKIP</button>
              )}
              <button data-testid="cin-story-close" onClick={() => { stopStory(); setPanel(null); }} aria-label="Close story" className="w-8 h-8 rounded-full border border-white/20 text-white/50 flex items-center justify-center"><X size={13} /></button>
            </div>
          </div>
          {story.loading ? (
            <p className="text-xs text-white/50 mt-3 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> VeerGi is clearing his throat…</p>
          ) : (
            <div className="mt-3 space-y-1.5" aria-live="polite">
              {lines.map((l, i) => (
                <div key={i}>
                  <p data-testid={`cin-subtitle-${i}`}
                    className={`text-sm leading-relaxed transition-colors duration-300 ${story.playing && story.line === i ? "text-[#F0C742] font-semibold" : story.playing ? "text-white/30" : "text-white/70"}`}>{l}</p>
                  {paSubs && paLines?.[i] && (
                    <p data-testid={`cin-subtitle-pa-${i}`} lang="pa"
                      className={`text-[13px] leading-relaxed transition-colors duration-300 ${story.playing && story.line === i ? "text-[#E8C97F]" : story.playing ? "text-white/25" : "text-white/65"}`}>{paLines[i]}</p>
                  )}
                </div>
              ))}
            </div>
          )}
          {!story.playing && !story.loading && (
            <button data-testid="cin-story-replay" onClick={playStory} className="mt-3 h-9 px-5 rounded-full bg-[#E25822] text-white text-[10px] font-black tracking-widest">PLAY NARRATION</button>
          )}
        </div>
      )}
      {panel === "ingredients" && (
        <div data-testid="cin-ingredients-panel" className="mx-4 mt-4 rounded-2xl border border-white/10 bg-[#121212] p-5 cin-rise">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">WHAT'S IN THE BOWL</p>
          <div className="mt-3 space-y-3">
            {(data.ingredients.length ? data.ingredients : [{ id: "d", name: data.dish.name, desc: data.dish.description }]).map((ing) => (
              <div key={ing.id} className="flex gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 shrink-0" />
                <div><p className="text-sm font-bold">{ing.name}</p><p className="text-xs text-white/55 leading-relaxed">{ing.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {panel === "nutrition" && (
        <div data-testid="cin-nutrition-panel" className="mx-4 mt-4 rounded-2xl border border-white/10 bg-[#121212] p-5 cin-rise">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">NUTRITION</p>
          {data.nutrition ? (
            <>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                {[["CALORIES", data.nutrition.calories], ["PROTEIN", data.nutrition.protein], ["CARBS", data.nutrition.carbs], ["FAT", data.nutrition.fat]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-[#1A1A1A] border border-white/5 py-3">
                    <p className="text-lg font-black text-[#D4AF37]">{v}</p><p className="text-[8px] tracking-widest text-white/40 font-bold">{k}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-white/35 mt-2">{data.nutrition.note}</p>
            </>
          ) : <p className="text-xs text-white/45 mt-2">Ask the counter for nutrition details on this dish.</p>}
        </div>
      )}
      {panel === "spice" && (
        <div data-testid="cin-spice-panel" className="mx-4 mt-4 rounded-2xl border border-white/10 bg-[#121212] p-5 cin-rise">
          <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-black">SPICE LEVEL</p>
          <div className="flex items-center gap-1.5 mt-3">
            {[1, 2, 3, 4, 5].map((n) => <Flame key={n} size={22} className={n <= data.spice.level ? "text-[#E25822]" : "text-white/15"} fill={n <= data.spice.level ? "#E25822" : "none"} />)}
            <span className="text-sm font-black ml-2">{data.spice.label}</span>
          </div>
          {data.spice.note && <p className="text-xs text-white/55 mt-2 leading-relaxed">{data.spice.note}</p>}
        </div>
      )}
      <div className="h-8" />
    </div>
  );
}
